/**
 * Remembers which agent conversation this tab is in.
 *
 * The panel mounts its chat only while open (so a stream is not held on every
 * page), and the fullscreen route is a separate mount entirely. Without this,
 * closing the panel or hitting expand started a brand new conversation and threw
 * the transcript away — and burned another of the harness's session slots.
 *
 * `sessionStorage` rather than `localStorage`: a transcript is a per-tab thing —
 * two tabs open on the agent should be two conversations, not one shared cursor.
 *
 * The harness *session* is not tab-scoped, though: it is a cookie, shared by
 * every tab. That difference is the source of a whole class of bug, so the
 * session's identity is recorded separately, in `localStorage`, below.
 */
const CONVERSATION_KEY = 'oscar.conversation';
/** Set when the user leaves fullscreen, read once by the dock on its next mount. */
const OPEN_INTENT_KEY = 'oscar.reopenPanel';

/**
 * Who the harness session cookie was minted for.
 *
 * `localStorage`, not `sessionStorage`: the cookie is browser-wide, so the
 * record of whose it is has to be too, or a second tab cannot tell whether the
 * session it is about to reuse belongs to the person now signed in.
 */
const SESSION_IDENTITY_KEY = 'oscar.session';

/**
 * The stored id is bound to the account that created it.
 *
 * Signing out of OpenWrench clears `localStorage`, but not `sessionStorage` and
 * not the harness's own `sid` cookie — that cookie carries no Max-Age, so it
 * lives until the browser closes. Without this binding, the next person to sign
 * in on the same browser would resume the previous user's conversation: they
 * would read that transcript, and their messages would run against the JWT the
 * harness still holds for the previous user.
 *
 * Checking identity here means that holds even when sign-out never runs — a
 * crash, an expired token, a restored tab.
 */
interface StoredConversation {
	email: string;
	/**
	 * And to the workspace it was started in.
	 *
	 * `changeWorkspace` swaps the OpenWrench token and reloads, but the harness
	 * cookie survives — so the reload skips the login exchange and would resume a
	 * conversation belonging to the workspace the user just left, with the agent
	 * still acting there. Email alone does not catch that: it is the same person.
	 */
	workspaceId: string;
	conversationId: string;
}

/** '' when unknown, which simply means no stored conversation will match. */
const activeWorkspaceId = (): string => {
	try {
		return String(window.localStorage.getItem('workspaceId') || '');
	} catch (storageError) {
		return '';
	}
};

/** Private-mode Safari throws on storage access; a missing id is recoverable. */
const read = (): StoredConversation | null => {
	try {
		const raw = window.sessionStorage.getItem(CONVERSATION_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch (storageError) {
		return null;
	}
};

/** Returns '' unless the stored conversation belongs to `email` in this workspace. */
export const readStoredConversationId = (email: string): string => {
	const stored = read();
	if (!stored || !email || stored.email !== email) {
		return '';
	}
	if (stored.workspaceId !== activeWorkspaceId()) {
		return '';
	}
	return stored.conversationId || '';
};

export const storeConversationId = (email: string, conversationId: string): void => {
	try {
		const payload: StoredConversation = {
			email,
			workspaceId: activeWorkspaceId(),
			conversationId,
		};
		window.sessionStorage.setItem(CONVERSATION_KEY, JSON.stringify(payload));
	} catch (storageError) {
		// Nothing to do — the chat still works, it just will not resume.
	}
};

/** Call on sign-out, so the next account starts clean. */
export const clearStoredConversation = (): void => {
	try {
		// The reopen flag is session state too: left behind on sign-out it would
		// open the next account's panel on its own.
		window.sessionStorage.removeItem(OPEN_INTENT_KEY);
		window.sessionStorage.removeItem(CONVERSATION_KEY);
		// The harness cookie can outlive this (no Max-Age, and the logout is best
		// effort). Dropping our record of whose it is means the next boot cannot
		// reuse it and must exchange a fresh login — which is the safe default.
		window.localStorage.removeItem(SESSION_IDENTITY_KEY);
	} catch (storageError) {
		// See above.
	}
};

/**
 * Whether the panel should reopen itself on the next mount.
 *
 * Expanding closes the dock and pushes the fullscreen route, and `App.tsx`
 * unmounts the whole top bar — the dock with it — while that route is active.
 * Coming back therefore remounted the dock closed, so shrink landed on the
 * launcher rather than the panel it came from.
 *
 * Recorded when the user actually leaves fullscreen, never when they enter it.
 * Setting it on expand left the flag alive if they walked away through the left
 * navigation instead, and the dock then opened Oscar over whatever unrelated
 * page they had chosen.
 */

export const storePanelOpenIntent = (): void => {
	try {
		window.sessionStorage.setItem(OPEN_INTENT_KEY, '1');
	} catch (storageError) {
		// Worst case the panel does not reopen — the conversation is still there.
	}
};

/**
 * Reads the intent and clears it, so it only ever survives one transition. Left
 * to linger it would reopen the panel on unrelated navigation later.
 */
export const takePanelOpenIntent = (): boolean => {
	try {
		const intent = window.sessionStorage.getItem(OPEN_INTENT_KEY) === '1';
		window.sessionStorage.removeItem(OPEN_INTENT_KEY);
		return intent;
	} catch (storageError) {
		return false;
	}
};

/**
 * The identity a harness session was exchanged for.
 *
 * `role` and `email` are checked against `/api/me` as well, which is the
 * authority. `workspaceId` is not: the harness has no notion of an OpenWrench
 * workspace, so a facility switch — same person, same role, different data —
 * is invisible to it and can only be caught here.
 */
export interface StoredSessionIdentity {
	email: string;
	role: string;
	workspaceId: string;
}

export const storeSessionIdentity = (email: string, role: string): void => {
	try {
		const payload: StoredSessionIdentity = {
			email,
			role,
			workspaceId: activeWorkspaceId(),
		};
		window.localStorage.setItem(SESSION_IDENTITY_KEY, JSON.stringify(payload));
	} catch (storageError) {
		// Unrecorded means unreusable, which is the safe way to fail.
	}
};

/**
 * Whether the live harness session may be reused for this person.
 *
 * False whenever there is any doubt — no record, unreadable storage, a
 * different account, role or workspace. The cost of a false negative is one
 * login exchange; the cost of a false positive is acting as somebody else.
 */
export const sessionIdentityMatches = (email: string, role: string): boolean => {
	try {
		const raw = window.localStorage.getItem(SESSION_IDENTITY_KEY);
		if (!raw) {
			return false;
		}
		const stored: StoredSessionIdentity = JSON.parse(raw);
		return (
			!!email &&
			stored.email === email &&
			stored.role === role &&
			stored.workspaceId === activeWorkspaceId()
		);
	} catch (storageError) {
		return false;
	}
};
